import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Loader2, Sparkles, Check, ArrowLeft, AlertCircle } from 'lucide-react'
import { useClarifyInterests, type ClarifyMessage } from '../../../api/series'
import type { SeriesUserProfile } from '../../../types/series'
import type { UseCase } from './use-case-step'

const WELCOME_MESSAGES: Record<UseCase, string> = {
  'busy-professional':
    "Welcome to your personal podcast. Tell me what you want help with, plus any goals or constraints that matter, and I'll shape the feed around that.",
  'student-learner':
    "Welcome to your study companion. Tell me what you're learning, where you're getting stuck, and what would make the podcast actually useful.",
  'curious-explorer':
    "Welcome to your curiosity feed. Tell me what you're curious about and what angle or context would make it worth listening to.",
}

const DEFAULT_WELCOME =
  "Welcome to your personal podcast. Describe the topics, goals, and context that matter to you, and I'll build around that."

const EXAMPLES_BY_USE_CASE: Record<UseCase, string[]> = {
  'busy-professional': [
    'AI developments in my industry, startup strategies',
    'Personal finance, investing strategies, market trends',
    'Tech news, SaaS developments, productivity tools',
  ],
  'student-learner': [
    'Machine learning research, neural networks',
    'Psychology fundamentals, cognitive science',
    'Software engineering, system design',
  ],
  'curious-explorer': [
    'Psychology, how the mind works, decision-making',
    'History and culture, ancient civilizations',
    'Space exploration, astronomy, the future',
  ],
}

const DEFAULT_EXAMPLES = [
  'Product design, startup growth, AI agents',
  'Personal finance, real estate investing',
  'Climate tech, renewable energy',
]

// Typewriter component for AI messages
function TypewriterText({ text, onComplete }: { text: string; onComplete?: () => void }) {
  const [displayedText, setDisplayedText] = useState('')

  useEffect(() => {
    let i = 0
    setDisplayedText('') // reset on new text
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.substring(0, i + 1))
        i++
      } else {
        clearInterval(timer)
        onComplete?.()
      }
    }, 15) // writing speed
    return () => clearInterval(timer)
  }, [text, onComplete])

  return <span>{displayedText}</span>
}

interface InterestsStepProps {
  value: string
  useCase: UseCase | null
  onChange: (data: { interests: string; userProfile?: SeriesUserProfile | null }) => void
  onComplete?: () => void
  onBack?: () => void
}

export function InterestsStep({ value, useCase, onChange, onComplete, onBack }: InterestsStepProps) {
  const [messages, setMessages] = useState<ClarifyMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // To handle the typewriting state of the very first welcome message
  const [isWelcomeTyping, setIsWelcomeTyping] = useState(true)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const clarify = useClarifyInterests()
  const hasInitialized = useRef(false)

  // Initialize with welcome message
  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    const welcomeMessage = useCase ? WELCOME_MESSAGES[useCase] : DEFAULT_WELCOME
    
    // If we already have interests saved, restore state instantly (no typing effect)
    if (value.trim()) {
      setIsWelcomeTyping(false)
      setMessages([
        { role: 'assistant', content: welcomeMessage },
        { role: 'user', content: value },
        { role: 'assistant', content: "Great choices! Those topics will make for an engaging podcast. You're all set to continue." }
      ])
      setIsComplete(true)
    } else {
      setMessages([{ role: 'assistant', content: welcomeMessage }])
    }
  }, [useCase, value])

  // Scroll to bottom when messages change or typing state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isWelcomeTyping])

  // Focus input after AI responds
  useEffect(() => {
    if (!clarify.isPending && !isComplete && messages.length > 0 && !isWelcomeTyping) {
      inputRef.current?.focus()
    }
  }, [clarify.isPending, isComplete, messages.length, isWelcomeTyping])

  const handleSend = (text: string = inputValue) => {
    if (!text.trim() || clarify.isPending) return

    const userMessage: ClarifyMessage = { role: 'user', content: text.trim() }
    const newMessages = [...messages, userMessage]
    const conversationHistory = newMessages.slice(1)
    const rootInterests = conversationHistory.find((message) => message.role === 'user')?.content ?? userMessage.content

    setMessages(newMessages)
    setInputValue('')
    setError(null)
    setIsWelcomeTyping(false) // just in case

    clarify.mutate(
      { interests: rootInterests, useCase, conversationHistory },
      {
        onSuccess: (response) => {
          setMessages((prev) => [...prev, { role: 'assistant', content: response.message }])
          if (response.isComplete) {
            setIsComplete(true)
            onChange({
              interests: rootInterests,
              userProfile: response.userProfile,
            })
          }
        },
        onError: (err) => {
          const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
          setError(errorMessage)
          setMessages((prev) => prev.slice(0, -1))
          setInputValue(userMessage.content)
        },
      }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleContinue = () => {
    onComplete?.()
  }

  const examples = useCase ? EXAMPLES_BY_USE_CASE[useCase] : DEFAULT_EXAMPLES

  return (
    <div className="flex flex-col h-full max-h-[600px]">
      <div className="mb-4 flex items-center gap-3 shrink-0">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100">
          <Sparkles className="h-5 w-5 text-brand-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">What interests you?</h2>
          <p className="text-sm text-foreground/60">Describe the topics, goals, and constraints you want the podcast to optimize for</p>
        </div>
      </div>

      {/* Chat messages */}
      <div className="mb-4 flex-1 overflow-y-auto rounded-2xl border border-black/5 bg-black/[0.02] p-4 flex flex-col gap-4">
        <AnimatePresence mode="popLayout">
          {messages.map((message, index) => {
            const isFirstWelcome = index === 0;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                    message.role === 'user'
                      ? 'bg-brand-600 text-white rounded-tr-sm'
                      : 'bg-white text-foreground ring-1 ring-black/5 rounded-tl-sm'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {/* Only typewriter the first welcome message if it's new, or the latest AI message if we're not complete. 
                        Simplest way: Just typewriter all new AI messages.
                        For now, we just Typewriter the very first welcome message for dramatic effect. */}
                    {message.role === 'assistant' && isFirstWelcome && isWelcomeTyping ? (
                      <TypewriterText text={message.content} onComplete={() => setIsWelcomeTyping(false)} />
                    ) : (
                      message.content
                    )}
                  </p>
                </div>
                
                {/* Show examples right after the welcome message finishes typing */}
                {isFirstWelcome && !isWelcomeTyping && messages.length === 1 && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-3 flex flex-wrap gap-2 max-w-[85%]"
                  >
                    {examples.map((ex, i) => (
                      <motion.button
                        key={i}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSend(ex)}
                        className="cursor-pointer text-left rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100"
                      >
                        {ex}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            )
          })}

          {clarify.isPending && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="flex items-center gap-3 rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm ring-1 ring-black/5">
                <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                <span className="text-sm text-foreground/50">Shaping your podcast...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} className="h-1" />
      </div>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 overflow-hidden"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input or Continue button */}
      <div className="shrink-0 space-y-3">
        {isComplete ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <button
              onClick={handleContinue}
              className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700 hover:shadow-md hover:shadow-brand-500/20 active:scale-[0.98]"
            >
              <Check className="h-4 w-4" />
              Continue
            </button>
            <button
              onClick={() => {
                setIsComplete(false)
                setMessages(prev => prev.slice(0, -1)) // Remove last AI message
                onChange({ interests: '', userProfile: null })
              }}
              className="w-full cursor-pointer text-center text-sm font-medium text-foreground/40 transition-colors hover:text-foreground/70"
            >
              Edit my interests
            </button>
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-3"
          >
            <div className="flex gap-2 relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isWelcomeTyping ? 'Wait for the assistant...' : "Type your interests here..."}
                disabled={clarify.isPending || isWelcomeTyping}
                className="flex-1 rounded-xl border border-black/10 bg-background px-4 py-3.5 pl-4 pr-14 text-sm text-foreground shadow-sm placeholder:text-foreground/30 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 disabled:opacity-50 disabled:bg-black/5"
              />
              <button
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || clarify.isPending || isWelcomeTyping}
                className="absolute right-1.5 top-1.5 bottom-1.5 flex aspect-square cursor-pointer items-center justify-center rounded-lg bg-brand-600 text-white transition-all hover:bg-brand-700 hover:shadow-md active:scale-95 disabled:pointer-events-none disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>

            {/* Back button */}
            {onBack && (
              <button
                onClick={onBack}
                className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-foreground/40 transition-colors hover:text-foreground/70"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}
